const GANTT_LEFT_COLUMN_ORDER = [
  "rowNumber",
  "open",
  "Panel",
  "taskNumber",
  "title",
  "status",
  "completionPercentage",
  "state",
  "statusDescription",
  "managers",
  "participants",
  "suppliers",
  "workspaces",
  "duration",
  "plannedStartDate",
  "plannedEndDate",
  "daysLeft",
  "actualStartDate",
  "actualEndDate",
  "description",
  "linksAndAttachments",
  "ganttBarColor",
  "ganttAncestors",
  "actualBarEndDate",
  "actualBarStartDate",
  "basePlanStartDate",
  "basePlanEndDate",
  "ganttGanttHtmlRight",
  "order",
  "canCreateTasks",
  "dependencies",
  "ganttDependencies",
] as const

const TASK_LIST_LEFT_COLUMN_ORDER = [
  "rowNumber",
  "open",
  "Panel",
  "taskNumber",
  "title",
  "status",
  "completionPercentage",
  "state",
  "statusDescription",
  "managers",
  "participants",
  "suppliers",
  "workspaces",
  "plannedStartDate",
  "plannedEndDate",
  "actualStartDate",
  "actualEndDate",
  "description",
  "linksAndAttachments",
  "ganttBarColor",
  "ganttAncestors",
  "duration",
  "daysLeft",
  "actualBarEndDate",
  "actualBarStartDate",
  "basePlanStartDate",
  "basePlanEndDate",
  "ganttGanttHtmlRight",
  "order",
  "canCreateTasks",
  "dependencies",
  "ganttDependencies",
] as const

const GANTT_RIGHT_COLUMN_ORDER = ["gantt"] as const
const TASK_LIST_RIGHT_COLUMN_ORDER = [] as const

export type LeftColumnName = (typeof GANTT_LEFT_COLUMN_ORDER)[number] | (typeof TASK_LIST_LEFT_COLUMN_ORDER)[number]
type RightColumnName = (typeof GANTT_RIGHT_COLUMN_ORDER)[number] | (typeof TASK_LIST_RIGHT_COLUMN_ORDER)[number]
export type ColumnName = LeftColumnName | RightColumnName

export type ColumnSettings = {
  leftColumnOrder: readonly LeftColumnName[]
  rightColumnOrder: readonly RightColumnName[]
  defaultVisibleColumns: ReadonlySet<ColumnName>
  defaultHiddenColumns: ReadonlySet<ColumnName>
  timeComponentEnabledHiddenColumns: ReadonlySet<ColumnName>
}

export const GANTT_COLUMN_SETTINGS: ColumnSettings = {
  leftColumnOrder: GANTT_LEFT_COLUMN_ORDER,
  rightColumnOrder: GANTT_RIGHT_COLUMN_ORDER,
  defaultVisibleColumns: new Set<ColumnName>([
    "open",
    "Panel",
    "taskNumber",
    "title",
    "status",
    "completionPercentage",
    "managers",
    "duration",
    "plannedStartDate",
    "plannedEndDate",
    "ganttBarColor",
  ]),
  defaultHiddenColumns: new Set<ColumnName>([]),
  timeComponentEnabledHiddenColumns: new Set<ColumnName>(["duration"]),
}

export const TASK_LIST_COLUMN_SETTINGS: ColumnSettings = {
  leftColumnOrder: TASK_LIST_LEFT_COLUMN_ORDER,
  rightColumnOrder: TASK_LIST_RIGHT_COLUMN_ORDER,
  defaultVisibleColumns: new Set<ColumnName>([
    "open",
    "Panel",
    "taskNumber",
    "title",
    "status",
    "completionPercentage",
    "statusDescription",
    "managers",
    "plannedStartDate",
    "plannedEndDate",
    "description",
    "linksAndAttachments",
  ]),
  defaultHiddenColumns: new Set<ColumnName>([]),
  timeComponentEnabledHiddenColumns: new Set<ColumnName>(["duration"]),
}

export const getColumnSettings = (showGantt: boolean): ColumnSettings => {
  return showGantt ? GANTT_COLUMN_SETTINGS : TASK_LIST_COLUMN_SETTINGS
}

export const getDefaultVisibleColumnNames = (showGantt: boolean, enableTimeComponent: boolean): ColumnName[] => {
  const columnSettings = getColumnSettings(showGantt)
  const visibleColumns = new Set(columnSettings.defaultVisibleColumns)

  columnSettings.defaultHiddenColumns.forEach((col) => visibleColumns.delete(col))

  if (enableTimeComponent) {
    columnSettings.timeComponentEnabledHiddenColumns.forEach((col) => visibleColumns.delete(col))
  }

  // Return columns in canonical order, filtered to only visible ones
  return [...columnSettings.leftColumnOrder, ...columnSettings.rightColumnOrder].filter((col) =>
    visibleColumns.has(col)
  )
}
